import { STUDY_SUBJECTS } from './studyCatalog';
import type { RolloverDraft, RolloverPlan } from './studyYearApi';

/** A learner's choice for a Task. `split` is a server ability the learner screen does not offer. */
export type TaskChoice = 'continue' | 'defer' | 'archive';
/** Assessment `defer` has the same server result as `continue`, so only two choices exist. */
export type AssessmentChoice = 'continue' | 'archive';
export type WrapUpStep = 1 | 2 | 3;
export const REFLECTION_MAX = 500;
export const MAX_YEAR_LEVEL = 12;
export const MIN_YEAR_LEVEL = 6;
/** The server accepts at most 20 subjects in a rollover plan. */
export const MAX_NEXT_TERM_SUBJECTS = 20;
/** The Term setup subject catalogue; the server checks added subjects against the same list. */
const CATALOGUE = new Map<string, string>(STUDY_SUBJECTS);

export const WRAP_UP_STEPS: Array<{ step: WrapUpStep; label: string }> = [
  { step: 1, label: 'Look back' },
  { step: 2, label: 'Unfinished work' },
  { step: 3, label: 'Next Term' },
];

export type WrapUpChoices = {
  tasks: Record<string, TaskChoice>;
  assessments: Record<string, AssessmentChoice>;
};

export type WrapUpState = {
  step: WrapUpStep;
  reflection: string;
  choices: WrapUpChoices;
  subjectKeys: string[];
  /** "Add a subject" (PRD §5.3): setup-catalogue keys that are not in the current Term. */
  addedSubjectKeys: string[];
  startsOn: string;
  endsOn: string;
  nextYearLevel: number;
};

const STORAGE_PREFIX = 'airbotix.study.wrap-up.v1';

export function wrapUpStorageKey(owner: string, fromTermId: string): string {
  return `${STORAGE_PREFIX}:${owner}:${fromTermId}`;
}

export function isYearWrapUp(draft: Pick<RolloverDraft, 'term_number'>): boolean {
  return draft.term_number >= 4;
}

/** "Term 4" for a Term wrap-up, "2027" for the year wrap-up. */
export function nextPeriodLabel(draft: Pick<RolloverDraft, 'term_number' | 'school_year'>) {
  return isYearWrapUp(draft) ? String(draft.school_year + 1) : `Term ${draft.term_number + 1}`;
}

export function initialWrapUpState(draft: RolloverDraft): WrapUpState {
  return {
    step: 1,
    reflection: '',
    choices: { tasks: {}, assessments: {} },
    subjectKeys: draft.subjects.map((subject) => subject.subject_key),
    addedSubjectKeys: [],
    startsOn: draft.starts_on ?? '',
    endsOn: draft.ends_on ?? '',
    nextYearLevel: Math.min(MAX_YEAR_LEVEL, (draft.year_level ?? MIN_YEAR_LEVEL - 1) + 1),
  };
}

/**
 * Keeps only the saved choices that still match the server draft. Items finished or removed on
 * another device drop out; new items stay unchosen.
 */
export function reconcileWrapUpState(saved: WrapUpState, draft: RolloverDraft): WrapUpState {
  const taskIds = new Set(draft.tasks.map((task) => task.id));
  const assessmentIds = new Set(draft.assessments.map((assessment) => assessment.id));
  const subjects = new Set(draft.subjects.map((subject) => subject.subject_key));
  const added = [...new Set(saved.addedSubjectKeys)];
  // A subject added here that the current Term now has (set on another device) stays chosen,
  // as a ticked subject of this Term.
  const nowInTerm = added.filter((key) => subjects.has(key));
  return {
    ...saved,
    reflection: saved.reflection.slice(0, REFLECTION_MAX),
    choices: {
      tasks: Object.fromEntries(
        Object.entries(saved.choices.tasks).filter(([id]) => taskIds.has(id)),
      ),
      assessments: Object.fromEntries(
        Object.entries(saved.choices.assessments).filter(([id]) => assessmentIds.has(id)),
      ),
    },
    subjectKeys: [
      ...new Set([...saved.subjectKeys.filter((key) => subjects.has(key)), ...nowInTerm]),
    ],
    addedSubjectKeys: added.filter((key) => CATALOGUE.has(key) && !subjects.has(key)),
  };
}

/** The catalogue name of a subject key, as Term setup shows it. */
export function catalogueSubjectName(key: string): string {
  return CATALOGUE.get(key) ?? key;
}

/** Setup-catalogue subjects that are neither in the current Term nor already added. */
export function addableSubjects(
  draft: Pick<RolloverDraft, 'subjects'>,
  state: Pick<WrapUpState, 'addedSubjectKeys'>,
): Array<{ subject_key: string; display_name: string }> {
  const taken = new Set([
    ...draft.subjects.map((subject) => subject.subject_key),
    ...state.addedSubjectKeys,
  ]);
  return STUDY_SUBJECTS.filter(([key]) => !taken.has(key)).map(([key, name]) => ({
    subject_key: key,
    display_name: name,
  }));
}

/** The number of subjects the next Term would have: ticked ones plus added ones. */
export function nextTermSubjectCount(
  state: Pick<WrapUpState, 'subjectKeys' | 'addedSubjectKeys'>,
): number {
  return state.subjectKeys.length + state.addedSubjectKeys.length;
}

/** A saved draft. One saved before "Add a subject" existed has no `addedSubjectKeys`. */
type SavedWrapUpState = Omit<WrapUpState, 'addedSubjectKeys'> & { addedSubjectKeys?: string[] };

function isSavedWrapUpState(value: unknown): value is SavedWrapUpState {
  if (!value || typeof value !== 'object') return false;
  const state = value as Partial<WrapUpState>;
  return (
    (state.addedSubjectKeys === undefined ||
      (Array.isArray(state.addedSubjectKeys) &&
        state.addedSubjectKeys.every((key) => typeof key === 'string'))) &&
    (state.step === 1 || state.step === 2 || state.step === 3) &&
    typeof state.reflection === 'string' &&
    typeof state.startsOn === 'string' &&
    typeof state.endsOn === 'string' &&
    typeof state.nextYearLevel === 'number' &&
    Array.isArray(state.subjectKeys) &&
    typeof state.choices === 'object' &&
    state.choices !== null &&
    typeof state.choices.tasks === 'object' &&
    typeof state.choices.assessments === 'object'
  );
}

/** Reads a device-only draft. Storage can be blocked, so every access is guarded. */
export function readWrapUpDraft(key: string): WrapUpState | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isSavedWrapUpState(parsed)) return null;
    return { ...parsed, addedSubjectKeys: parsed.addedSubjectKeys ?? [] };
  } catch {
    return null;
  }
}

export function saveWrapUpDraft(key: string, state: WrapUpState): boolean {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

export function clearWrapUpDraft(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

/** Every unfinished item needs a choice before step 2 can continue. */
export function choicesComplete(draft: RolloverDraft, choices: WrapUpChoices): boolean {
  return (
    draft.tasks.every((task) => choices.tasks[task.id] !== undefined) &&
    draft.assessments.every((assessment) => choices.assessments[assessment.id] !== undefined)
  );
}

export function chosenCount(draft: RolloverDraft, choices: WrapUpChoices): number {
  return (
    draft.tasks.filter((task) => choices.tasks[task.id] !== undefined).length +
    draft.assessments.filter((assessment) => choices.assessments[assessment.id] !== undefined)
      .length
  );
}

export function bringEverything(draft: RolloverDraft): WrapUpChoices {
  return {
    tasks: Object.fromEntries(draft.tasks.map((task) => [task.id, 'continue' as const])),
    assessments: Object.fromEntries(
      draft.assessments.map((assessment) => [assessment.id, 'continue' as const]),
    ),
  };
}

/** The rollover plan for preview and commit. It never contains `split` or Assessment `defer`. */
export function buildRolloverPlan(draft: RolloverDraft, state: WrapUpState): RolloverPlan {
  return {
    academic_year_id: draft.academic_year_id,
    from_term_id: draft.from_term_id,
    // Ticked subjects of this Term in their order, then added subjects in the order added.
    subject_keys: [
      ...draft.subjects
        .map((subject) => subject.subject_key)
        .filter((key) => state.subjectKeys.includes(key)),
      ...state.addedSubjectKeys.filter(
        (key) => !draft.subjects.some((subject) => subject.subject_key === key),
      ),
    ],
    starts_on: state.startsOn || null,
    ends_on: state.endsOn || null,
    tasks: draft.tasks.map((task) => ({
      id: task.id,
      action: state.choices.tasks[task.id] ?? 'continue',
    })),
    assessments: draft.assessments.map((assessment) => ({
      id: assessment.id,
      action: state.choices.assessments[assessment.id] ?? 'continue',
    })),
    reflection: state.reflection.trim(),
    ...(isYearWrapUp(draft)
      ? { next_school_year: draft.school_year + 1, next_year_level: state.nextYearLevel }
      : {}),
  };
}

export type WrapUpCounts = { kept: number; saved: number; dropped: number };

export function wrapUpCounts(plan: RolloverPlan): WrapUpCounts {
  const actions = [...plan.tasks, ...plan.assessments].map((item) => item.action);
  return {
    kept: actions.filter((action) => action === 'continue').length,
    saved: actions.filter((action) => action === 'defer').length,
    dropped: actions.filter((action) => action === 'archive').length,
  };
}

/** "5 tasks come with you, 1 is saved for later, 2 are dropped." Zero parts are left out. */
export function countsSentence(counts: WrapUpCounts): string {
  const parts = [
    counts.kept > 0
      ? `${counts.kept} ${counts.kept === 1 ? 'task comes' : 'tasks come'} with you`
      : '',
    counts.saved > 0 ? `${counts.saved} ${counts.saved === 1 ? 'is' : 'are'} saved for later` : '',
    counts.dropped > 0 ? `${counts.dropped} ${counts.dropped === 1 ? 'is' : 'are'} dropped` : '',
  ].filter(Boolean);
  if (parts.length === 0) return '';
  return `${parts.join(', ')}.`;
}

/** The step 1 count line, from the server draft. */
export function unfinishedSentence(draft: RolloverDraft): string {
  const tasks = draft.tasks.length;
  const assessments = draft.assessments.length;
  if (tasks + assessments === 0) return 'Nothing left over. Nice.';
  const taskPart = `${tasks} unfinished ${tasks === 1 ? 'task' : 'tasks'}`;
  const assessmentPart = `${assessments} ${assessments === 1 ? 'assessment' : 'assessments'}`;
  return `You have ${taskPart} and ${assessmentPart} to sort out.`;
}

/** Ids that appeared or disappeared between two server drafts (a conflict on another device). */
export function draftChanges(before: RolloverDraft, after: RolloverDraft) {
  const beforeIds = new Set([...before.tasks, ...before.assessments].map((item) => item.id));
  const afterIds = new Set([...after.tasks, ...after.assessments].map((item) => item.id));
  return {
    added: [...after.tasks, ...after.assessments]
      .filter((item) => !beforeIds.has(item.id))
      .map((item) => item.id),
    removed: [...before.tasks, ...before.assessments]
      .filter((item) => !afterIds.has(item.id))
      .map((item) => item.title),
  };
}
